import { normalizeSliderAspectRatio, normalizeTileAspectRatio } from '@/components/scenario/aspectRatio'
import { DEFAULT_BOX_ENTRY_CTA_TEXT, DEFAULT_BOX_ENTRY_TITLE, type BoxConfig } from '@/components/scenario/boxConfig'
import { normalizeEmbedFont } from '@/components/scenario/embedFont'
import type { PopupConfig } from '@/components/scenario/popupCta'
import { normalizeThumbnailRounded } from '@/components/scenario/thumbnailCorner'
import { FC } from 'react'
import { ScenarioDisplayType, ScenarioWithNodesAndVideoUrls } from '../../../orval/generated/public/model'
import { Hotspot, InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import BoxPlayer from '../box'
import { VideoModal } from '../modal'
import SliderPlayer from '../slider-player'
import StandalonePlayer from '../StandalonePlayer'
import TilePlayer from '../TilePlayer'
import WidePlayer from '../wide'
import PopupPlayer from './PopupPlayer'

type SliderConfig = {
  videoOrder?: string[]
  aspectRatio?: string
  thumbnailRounded?: boolean
  embedFont?: string
}

type TileConfig = {
  columns?: number
  mobileColumns?: number
  aspectRatio?: string
  videoOrder?: string[]
  thumbnailRounded?: boolean
  embedFont?: string
}

export type EmbeddingAiInterviewPresentationProps = {
  scenarioId: string
  scenario: ScenarioWithNodesAndVideoUrls
  interactiveConfig: PlayerConfig
  isPlayerOpen: boolean
  /**
   * 強制全画面オーバーレイモード。
   * scenario.displayType (SLIDER / TILE / POPUP) を無視して、
   * TILE / SLIDER 表示で本来モーダル表示に使われる VideoModal を直接描画する。
   * parent-bridge 経由で親ページ直下に mount された際の動作モード。
   *
   * POPUP 型シナリオ専用の `InteractiveVideoPlayer` ではなく、
   * `VideoModal`（PCVideoModal / SPVideoModal を内部で切り替え）を使うのは、
   * 野鶴亭等の TILE / SLIDER 型シナリオでは動画ノードのデータ構造が
   * InteractiveVideoPlayer の想定と異なり、video 要素が描画されないため。
   */
  forceOverlay?: boolean
  /**
   * forceOverlay 時に最初に再生する動画の videoId。
   * scenario.config.videoOrder と scenario.nodes から該当 index を逆引きして
   * VideoModal の initialVideoIndex に変換する。
   * 該当が無ければ 0（先頭）から再生する。
   */
  initialVideoId?: string
  /**
   * Scriptタグの `data-mode` 属性。
   * - `'single'`: data-video-id で指定された動画のサムネイルを単独表示し、
   *    クリック時に既存の VideoModal でプレイリスト全体を切替視聴できる。
   * - undefined / その他: scenario.displayType に従う既存挙動。
   */
  mode?: string
  /**
   * mode='single' で単独表示する動画 ID（Scriptタグの `data-video-id` 属性）。
   */
  modeVideoId?: string
  onThumbnailClick: () => void
  onClosePlayer: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
  /** 動画内のクリック領域が押された（ワイド型のみ・計測用・ADR-0022） */
  onHotspotClick?: (hotspot: Hotspot) => void | Promise<void>
}

const EmbeddingAiInterviewPresentation: FC<EmbeddingAiInterviewPresentationProps> = ({
  scenarioId,
  scenario,
  interactiveConfig,
  isPlayerOpen,
  forceOverlay = false,
  initialVideoId,
  mode,
  modeVideoId,
  onThumbnailClick,
  onClosePlayer,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
  onHotspotClick,
}) => {
  const getPopupFallbackThumbnail = () => {
    const initialNode =
      scenario.nodes.find((node) => node.nodeKey === interactiveConfig.initialNodeId) ?? scenario.nodes[0]
    if (!initialNode) {
      return { animatedUrl: null, url: null, mimeType: null }
    }

    return {
      animatedUrl: initialNode.animatedThumbnailUrl,
      url: initialNode.thumbnailUrl,
      mimeType: 'image/jpeg',
    }
  }

  // forceOverlay モードでは scenario.displayType に関係なく
  // VideoModal（中で PCVideoModal / SPVideoModal を切替）を強制描画する。
  // parent-bridge 経由で親ページ全体に全画面モーダルを展開する用途で使う。
  if (forceOverlay) {
    // SLIDER / TILE 共通で config.videoOrder を取得する。
    // POPUP 型シナリオに forceOverlay を適用するケースは想定しないが、
    // config が無ければ全 nodes を順序通りに使うフォールバックを入れて安全側に倒す。
    const config = (scenario.config as { videoOrder?: string[]; embedFont?: string }) || {}
    const videoOrder = config.videoOrder || []
    // initialVideoId から initialVideoIndex を逆引きする。
    // - videoOrder が指定されていれば videoOrder 内の index
    // - 指定が無ければ scenario.nodes 内の index
    // - 該当が無ければ 0（先頭）にフォールバック
    let initialVideoIndex = 0
    if (initialVideoId) {
      if (videoOrder.length > 0) {
        const idx = videoOrder.indexOf(initialVideoId)
        if (idx >= 0) initialVideoIndex = idx
      } else {
        const idx = scenario.nodes.findIndex((n) => n.videoId === initialVideoId)
        if (idx >= 0) initialVideoIndex = idx
      }
    }
    return (
      <VideoModal
        nodes={scenario.nodes}
        videoOrder={videoOrder}
        initialVideoIndex={initialVideoIndex}
        isOpen={isPlayerOpen}
        onClose={onClosePlayer}
        scenarioId={scenarioId}
        ctaUrl={scenario.ctaUrl}
        ctaLabel={scenario.ctaLabel}
        embedFont={normalizeEmbedFont(config.embedFont)}
      />
    )
  }

  // mode='single' (単独サムネイル埋め込みScript) の場合は scenario.displayType を無視して
  // 単独サムネイル表示する。クリック時に既存 VideoModal でプレイリスト全体を切替視聴可能。
  if (mode === 'single' && modeVideoId) {
    const config = (scenario.config as { videoOrder?: string[]; thumbnailRounded?: boolean; embedFont?: string }) || {}
    const videoOrder = config.videoOrder || []
    return (
      <StandalonePlayer
        nodes={scenario.nodes}
        videoOrder={videoOrder}
        videoId={modeVideoId}
        thumbnailRounded={normalizeThumbnailRounded(config.thumbnailRounded)}
        embedFont={normalizeEmbedFont(config.embedFont)}
        scenarioId={scenarioId}
      />
    )
  }

  // SLIDER型の場合
  if (scenario.displayType === ScenarioDisplayType.SLIDER) {
    const config = scenario.config as SliderConfig
    const videoOrder = config?.videoOrder || []
    const aspectRatio = normalizeSliderAspectRatio(config?.aspectRatio)

    return (
      <SliderPlayer
        nodes={scenario.nodes}
        videoOrder={videoOrder}
        aspectRatio={aspectRatio}
        thumbnailRounded={normalizeThumbnailRounded(config?.thumbnailRounded)}
        embedFont={normalizeEmbedFont(config?.embedFont)}
        scenarioId={scenarioId}
        ctaUrl={scenario.ctaUrl}
        ctaLabel={scenario.ctaLabel}
      />
    )
  }

  // TILE型の場合
  if (scenario.displayType === ScenarioDisplayType.TILE) {
    const config = scenario.config as TileConfig
    const videoOrder = config?.videoOrder || []
    const columns = config?.columns || 3
    const mobileColumns = config?.mobileColumns || 2
    const aspectRatio = normalizeTileAspectRatio(config?.aspectRatio)

    return (
      <TilePlayer
        nodes={scenario.nodes}
        videoOrder={videoOrder}
        columns={columns}
        mobileColumns={mobileColumns}
        aspectRatio={aspectRatio}
        thumbnailRounded={normalizeThumbnailRounded(config?.thumbnailRounded)}
        embedFont={normalizeEmbedFont(config?.embedFont)}
        scenarioId={scenarioId}
        ctaUrl={scenario.ctaUrl}
        ctaLabel={scenario.ctaLabel}
      />
    )
  }

  // BOX型: ページ内の入口バナー → クリックでモーダル（分岐ボタンのみで進む）
  if (scenario.displayType === ScenarioDisplayType.BOX) {
    const boxConfig = (scenario.config as BoxConfig) || {}
    // 入口サムネはシナリオ設定（popupThumbnailUrl）優先、無ければ初期ノードの動画サムネ。
    const fallback = getPopupFallbackThumbnail()
    const entryThumbnailUrl = scenario.popupThumbnailUrl ?? fallback.url
    const entryThumbnailMimeType = scenario.popupThumbnailUrl ? scenario.popupThumbnailMimeType : fallback.mimeType

    return (
      <BoxPlayer
        scenarioId={scenarioId}
        interactiveConfig={interactiveConfig}
        nodes={scenario.nodes}
        isPlayerOpen={isPlayerOpen}
        entryThumbnailUrl={entryThumbnailUrl}
        entryThumbnailMimeType={entryThumbnailMimeType}
        entryTitle={boxConfig.boxEntryTitle || DEFAULT_BOX_ENTRY_TITLE}
        entryCtaText={boxConfig.boxEntryCtaText || DEFAULT_BOX_ENTRY_CTA_TEXT}
        onThumbnailClick={onThumbnailClick}
        onClosePlayer={onClosePlayer}
        onVideoEnd={onVideoEnd}
        onVideoChange={onVideoChange}
        onButtonClick={onButtonClick}
      />
    )
  }

  // WIDE / WIDE_SINGLE: ページ内に横型16:9のポスター → クリックでモーダル再生（BOX と同じ開き方）。
  // WIDE_SINGLE はノードが1つ・分岐ボタンなしなので、同じプレイヤーで自然に「1本だけ再生→終了画面」になる
  if (
    scenario.displayType === ScenarioDisplayType.WIDE ||
    scenario.displayType === ScenarioDisplayType.WIDE_SINGLE
  ) {
    const fallback = getPopupFallbackThumbnail()
    const posterUrl = scenario.popupThumbnailUrl ?? fallback.url
    const posterMimeType = scenario.popupThumbnailUrl ? scenario.popupThumbnailMimeType : fallback.mimeType
    const initialNode =
      scenario.nodes.find((node) => node.nodeKey === interactiveConfig.initialNodeId) ?? scenario.nodes[0]

    return (
      <WidePlayer
        scenarioId={scenarioId}
        interactiveConfig={interactiveConfig}
        posterUrl={posterUrl}
        posterMimeType={posterMimeType}
        posterTitle={initialNode?.video?.title || initialNode?.title}
        posterDuration={initialNode?.video?.duration}
        onStart={onThumbnailClick}
        onClose={onClosePlayer}
        onVideoEnd={onVideoEnd}
        onVideoChange={onVideoChange}
        onButtonClick={onButtonClick}
        onHotspotClick={onHotspotClick}
      />
    )
  }

  // POPUP型（デフォルト）
  const popupFallbackThumbnail = getPopupFallbackThumbnail()
  // ホバー時 CTA の文言。管理画面の「ポップアップ設定」で scenario.config.popupCtaText
  // に保存された値がここから流し込まれる。未設定なら既定値。
  const popupConfig = (scenario.config as PopupConfig) || {}
  return (
    <PopupPlayer
      scenarioId={scenarioId}
      interactiveConfig={interactiveConfig}
      isPlayerOpen={isPlayerOpen}
      fallbackAnimatedThumbnailUrl={popupFallbackThumbnail.animatedUrl}
      fallbackThumbnailUrl={popupFallbackThumbnail.url}
      fallbackThumbnailMimeType={popupFallbackThumbnail.mimeType}
      ctaText={popupConfig.popupCtaText || undefined}
      onThumbnailClick={onThumbnailClick}
      onClosePlayer={onClosePlayer}
      onVideoEnd={onVideoEnd}
      onVideoChange={onVideoChange}
      onButtonClick={onButtonClick}
    />
  )
}

export default EmbeddingAiInterviewPresentation
